import React, { useMemo } from "react";
import styles from "./KeyEventsChart.module.css";
import {
  KEY_EVENTS,
  KEY_EVENTS_MONTHS,
  KEY_EVENTS_START,
  KEY_EVENTS_END,
  type KeyEvent,
} from "./keyEventsData";

const TOTAL_DAYS =
  (KEY_EVENTS_END.getTime() - KEY_EVENTS_START.getTime()) /
  (1000 * 60 * 60 * 24);

const CAT_CLASS: Record<string, string> = {
  election: "catElection",
  economic: "catEconomic",
  climate: "catClimate",
  industry: "catIndustry",
  summit: "catSummit",
};

function dayOffset(dateStr: string): number {
  const d = new Date(dateStr);
  return (d.getTime() - KEY_EVENTS_START.getTime()) / (1000 * 60 * 60 * 24);
}

function pct(days: number): number {
  return (days / TOTAL_DAYS) * 100;
}

const EventRow: React.FC<{ ev: KeyEvent }> = ({ ev }) => {
  const startDay = dayOffset(ev.start);
  const endDay = dayOffset(ev.end);
  const duration = Math.max(endDay - startDay, 1);
  const catClass = styles[CAT_CLASS[ev.cat] ?? ""];
  const statusClass =
    ev.status === "expected"
      ? styles.barExpected
      : ev.status === "tbc"
        ? styles.barTbc
        : "";

  return (
    <div className={styles.eventRow}>
      <div className={styles.eventLabel} title={ev.name}>
        {ev.name}
      </div>
      <div className={styles.eventTimeline}>
        <div className={styles.gridLines}>
          {KEY_EVENTS_MONTHS.map((_, i) => (
            <div key={i} className={styles.gridLine} />
          ))}
        </div>
        <div
          className={`${styles.eventBar} ${catClass} ${statusClass}`}
          style={{
            left: `${pct(startDay)}%`,
            width: `${pct(duration)}%`,
            minWidth: 4,
          }}
        />
        {ev.location && (
          <div
            className={styles.eventLocation}
            style={{ left: `${pct(startDay + duration) + 0.3}%` }}
          >
            {ev.location}
          </div>
        )}
      </div>
    </div>
  );
};

export const KeyEventsChart: React.FC = () => {
  const sortedEvents = useMemo(
    () => [...KEY_EVENTS].sort((a, b) => a.start.localeCompare(b.start)),
    [],
  );

  return (
    <div id="block-key-events-chart" className={styles.wrapper}>
      <h2 className={styles.title}>Global Key Events</h2>
      <div className={styles.subtitle}>April 2026 – April 2027</div>

      <div className={styles.legend}>
        <div className={styles.legendSection}>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.catElection}`} />
            Election
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.catEconomic}`} />
            Economic
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.catClimate}`} />
            Climate
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.catIndustry}`} />
            Industry
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.catSummit}`} />
            Summit
          </div>
        </div>
        <div className={styles.legendSeparator} />
        <div className={styles.legendSection}>
          <div className={styles.legendItem}>
            <div className={`${styles.legendBar} ${styles.catSummit}`} />
            Confirmed
          </div>
          <div className={styles.legendItem}>
            <div
              className={`${styles.legendBar} ${styles.catSummit} ${styles.barExpected}`}
            />
            Expected
          </div>
          <div className={styles.legendItem}>
            <div
              className={`${styles.legendBar} ${styles.barTbc} ${styles.catSummit}`}
            />
            Date TBC
          </div>
        </div>
      </div>

      <div className={styles.chartContainer}>
        <div className={styles.monthHeaders}>
          {KEY_EVENTS_MONTHS.map((m) => (
            <div key={m} className={styles.monthHeader}>
              {m}
            </div>
          ))}
        </div>
        <div>
          {sortedEvents.map((ev, i) => (
            <EventRow key={i} ev={ev} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default KeyEventsChart;
